/* eslint-disable @typescript-eslint/no-explicit-any */
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkHtml from "remark-html";
import { unified, Plugin } from "unified";
import type { Root, Link } from "mdast";

const targetBlank: Plugin<[], Root> = () => {
  return (tree: Root) => {
    const walk = (node: any) => {
      if (node.type === "link") {
        const link = node as Link;
        link.data ??= {};
        link.data.hProperties ??= {};
        link.data.hProperties.target = "_blank";
        link.data.hProperties.rel = "noopener noreferrer";
      }
      if (node.children) (node.children as any[]).forEach(walk);
    };
    walk(tree);
  };
};

export async function markdownToHtml(markdown: string): Promise<string> {
  const result = await unified().use(remarkParse).use(remarkGfm).use(remarkHtml, { sanitize: false }).process(markdown);

  return String(result);
}

export function markdownToHtmlSync(markdown: string): string {
  const result = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(targetBlank)
    .use(remarkHtml, { sanitize: false })
    .processSync(markdown)
    .toString();
  return result;
}

/**
 * Checks if a URL should be excluded from link conversion.
 * URLs that start with "https://zapistorage" and contain "/public/" should not be converted.
 */
function shouldExcludeFromLinkConversion(url: string): boolean {
  return url.startsWith("https://zapistorage") && (url.includes("/public/") || url.includes("/juicer-images/"));
}

/**
 * Converts markdown links to HTML links and fixes common URL typos
 * Note: We don't escape quotes here since we're saving directly to the database,
 * not embedding in JSON. The backend's convertMarkdownLinksToHtml escapes quotes
 * for JSON context, but the frontend saves directly to the database.
 */
export function convertMarkdownLinksToHtml(response: string): string {
  // First, fix common URL typos (like httpshttps:// -> https://)
  let fixedResponse = response.replace(/httpshttps:\/\//g, "https://");
  fixedResponse = fixedResponse.replace(/httphttp:\/\//g, "http://");

  // Match markdown links: [text](url)
  // Changed from [^\]]+ to [^\[]*? to avoid matching nested brackets incorrectly
  // This ensures we match the innermost link pattern first (e.g., [filename](url)
  // instead of [DOC: [filename](url) when both are present)
  const markdownLinkRegex = /(?<!!)\[([^[]*?)\]\((https?:\/\/[^)]+)\)/g;

  return fixedResponse.replace(markdownLinkRegex, (match, linkText, url) => {
    // Don't convert zapistorage public URLs to anchor tags
    if (shouldExcludeFromLinkConversion(url)) {
      return match;
    }
    // Don't escape quotes - we're saving directly to database, not embedding in JSON
    // Return HTML link with normal quotes for TipTap to parse correctly
    return `<a target="_blank" rel="noopener noreferrer" class="underline text-blue-500 hover:text-blue-600 cursor-pointer" href="${url}">${linkText}</a>`;
  });
}
